import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

function buildDecades(minYear, maxYear) {
  const map = {};
  for (let y = minYear; y <= maxYear; y++) {
    const d = Math.floor(y / 10) * 10;
    if (!map[d]) map[d] = [];
    map[d].push(y);
  }
  return map;
}

export default function YearPickerAccordion({
  value,
  onChange,
  minYear,
  maxYear,
  'aria-label': ariaLabel,
  sx,
}) {
  const numValue = value !== undefined && value !== null && value !== '' ? Number(value) : null;

  const currentDecade = Math.floor(new Date().getFullYear() / 10) * 10;
  const selectedDecade = numValue !== null ? Math.floor(numValue / 10) * 10 : currentDecade;
  const initialOpenDecade = selectedDecade !== currentDecade ? selectedDecade : currentDecade;

  const [open, setOpen] = useState(false);
  const [openDecades, setOpenDecades] = useState(() => new Set([initialOpenDecade]));
  const [focusedYear, setFocusedYear] = useState(null);

  const triggerRef = useRef(null);
  const listboxRef = useRef(null);
  const containerRef = useRef(null);
  const valueRef = useRef(numValue);
  valueRef.current = numValue;

  // When value changes externally (e.g. scenario load), ensure its decade is open
  useEffect(() => {
    if (numValue !== null) {
      const d = Math.floor(numValue / 10) * 10;
      setOpenDecades(prev => (prev.has(d) ? prev : new Set([...prev, d])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Focus listbox and seed keyboard-focus when opened
  useEffect(() => {
    if (open) {
      listboxRef.current?.focus();
      setFocusedYear(valueRef.current);
    }
  }, [open]);

  // Scroll focused year into view — only within the year list container, never the page
  useEffect(() => {
    if (focusedYear === null || !listboxRef.current) return;
    // Defer until after React has committed the DOM update
    const id = setTimeout(() => {
      const el = listboxRef.current?.querySelector(`[data-year="${focusedYear}"]`);
      if (!el) return;
      // Walk up to find the scrollable year-list container (direct parent of year items)
      const container = el.parentElement;
      if (!container || container.scrollHeight <= container.clientHeight) return;
      const elRect  = el.getBoundingClientRect();
      const ctRect  = container.getBoundingClientRect();
      const relTop    = elRect.top  - ctRect.top  + container.scrollTop;
      const relBottom = elRect.bottom - ctRect.top + container.scrollTop;
      if (relTop < container.scrollTop) {
        container.scrollTop = relTop;
      } else if (relBottom > container.scrollTop + container.clientHeight) {
        container.scrollTop = relBottom - container.clientHeight;
      }
    }, 0);
    return () => clearTimeout(id);
  }, [focusedYear]);

  const decades = buildDecades(minYear, maxYear);
  const decadeKeys = Object.keys(decades).map(Number).sort((a, b) => a - b);

  const getVisibleYears = () =>
    decadeKeys.flatMap(d => (openDecades.has(d) ? decades[d] : []));

  const toggleDecade = (decade) => {
    setOpenDecades(prev => {
      const next = new Set(prev);
      if (next.has(decade)) next.delete(decade);
      else next.add(decade);
      return next;
    });
  };

  const selectYear = (year) => {
    onChange(year);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleTriggerClick = () => setOpen(o => !o);

  const handleTriggerKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleListKeyDown = (e) => {
    const visible = getVisibleYears();
    const idx = focusedYear !== null ? visible.indexOf(focusedYear) : -1;

    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedYear(idx < visible.length - 1 ? visible[idx + 1] : visible[0]);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedYear(idx > 0 ? visible[idx - 1] : visible[visible.length - 1]);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedYear !== null) selectYear(focusedYear);
    }
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative', ...sx }}>

      {/* ── Trigger ── */}
      <Box
        ref={triggerRef}
        component="button"
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        sx={{
          width: '100%',
          height: '36px',
          border: `1.5px solid ${open ? '#008080' : 'rgba(0,0,0,0.5)'}`,
          borderRadius: open ? '8px 8px 0 0' : '8px',
          fontSize: '13px',
          fontFamily: 'inherit',
          background: '#fafcfe',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '10px',
          color: '#1a2c5b',
          outline: 'none',
          '&:focus-visible': {
            borderColor: '#008080',
            outline: '2px solid #008080',
            outlineOffset: '2px',
          },
          '&:hover': {
            borderColor: '#008080',
          },
        }}
      >
        <Box component="span" sx={{ fontSize: '13px', fontWeight: 600 }}>
          {numValue !== null ? numValue : ''}
        </Box>
        <Box component="span" aria-hidden="true" sx={{ fontSize: '10px', color: '#5E6069' }}>
          {open ? '▲' : '▼'}
        </Box>
      </Box>

      {/* ── Dropdown ── */}
      {open && (
        <Box
          ref={listboxRef}
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={0}
          onKeyDown={handleListKeyDown}
          sx={{
            position: 'absolute',
            top: '100%',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            zIndex: 1300,
            background: '#fff',
            border: '1.5px solid #008080',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 4px 16px rgba(0,0,0,.12)',
            outline: 'none',
          }}
        >
          {decadeKeys.map((decade, decadeIdx) => (
            <Box key={decade} role="group" aria-label={`${decade}s`}>

              {/* Decade header */}
              <Box
                component="button"
                type="button"
                tabIndex={-1}
                onClick={() => toggleDecade(decade)}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '10px',
                  py: '5px',
                  background: '#f8fafc',
                  border: 'none',
                  borderTop: decadeIdx === 0 ? 'none' : '1px solid #edf3f8',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#008080',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                  textAlign: 'right',
                  '&:hover': { background: '#f0f4f8' },
                }}
              >
                {/* In RTL: text is flex-start (right), arrow is flex-end (left) */}
                <Box component="span" sx={{ flex: 1, textAlign: 'right' }}>
                  {decade}s
                </Box>
                <Box component="span" aria-hidden="true" sx={{ fontSize: '9px', ml: '6px' }}>
                  {openDecades.has(decade) ? '▼' : '►'}
                </Box>
              </Box>

              {/* Year options — scrollable within decade */}
              {openDecades.has(decade) && (
              <Box data-year-list sx={{ maxHeight: '168px', overflowY: 'auto' }}>
              {decades[decade].map(year => {
                  const isSelected = year === numValue;
                  const isFocused  = year === focusedYear;
                  return (
                    <Box
                      key={year}
                      data-year={year}
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={-1}
                      onClick={() => selectYear(year)}
                      sx={{
                        px: '14px',
                        py: '6px',
                        fontSize: '13px',
                        color: isSelected ? '#008080' : '#1a2c5b',
                        fontWeight: isSelected ? 700 : 400,
                        background: isSelected
                          ? 'rgba(0,128,128,0.12)'
                          : isFocused
                          ? '#f0f4f8'
                          : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '&:hover': {
                          background: isSelected ? 'rgba(0,128,128,0.12)' : '#f0f4f8',
                        },
                      }}
                    >
                      <Box component="span" sx={{ flex: 1, textAlign: 'right' }}>
                        {year}
                      </Box>
                      {isSelected && (
                        <Box component="span" aria-hidden="true" sx={{ fontSize: '11px', ml: '6px' }}>
                          ✓
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
